import classNames from "classnames";
import React, { memo, useEffect, useMemo, useRef, useState } from "react";
import { FormSelectItem } from "../../../../Blocks/ProvetOnlineBooking/types/formSelectItem";
import {
  Button,
  ButtonColor,
  ButtonSize,
  ButtonType,
  ButtonVariant
} from "../../../../Shared/Buttons/React";
import { SearchInput } from "../../SearchInput/React";
import { CountryCodeSelectItems } from "./countryCodeSelectItems";
import { getCountryCodeByLanguage } from "./phoneNumberInputUtilities";

interface CountryCodeSelectProps {
  language?: string;
  value?: FormSelectItem;
  onChange?: (item: FormSelectItem, isUserAction?: boolean) => void;
  disabled?: boolean;
  name?: string;
  id?: string;
  required?: boolean;
  title?: string;
  onBlur?: () => void;
  allowSearch?: boolean;
  searchPlaceholder?: string;
  ariaLabel?: string;
}

export const CountryCodeSelect = memo(function CountryCodeSelect({
  language,
  value,
  onChange,
  disabled = false,
  name,
  id,
  required,
  title,
  onBlur,
  allowSearch = true,
  searchPlaceholder = "Search countries...",
  ariaLabel = "Country code selection"
}: CountryCodeSelectProps) {
  // Find initial country code by language (e.g., "sv-SE" -> "+46")
  const initialItem = language
    ? getCountryCodeByLanguage(language)
    : CountryCodeSelectItems[0];

  const [selected, setSelected] = useState<FormSelectItem>(value || initialItem);
  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [focusedIndex, setFocusedIndex] = useState<number>(-1);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Keep value in sync with selectedProp
  useEffect(() => {
    if (value && value.value !== selected.value) {
      setSelected(value);
    }
  }, [value, selected.value]);

  // Notify parent of initial value on mount (not a user action)
  useEffect(() => {
    if (onChange && selected) {
      onChange(selected, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update selected if language changes and no value prop is provided
  useEffect(() => {
    if (!value && language) {
      const newSelected = getCountryCodeByLanguage(language);

      if (newSelected) {
        setSelected(newSelected);
      }
    }
  }, [language, value]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  // Dropdown animation
  useEffect(() => {
    if (dropdownRef.current) {
      const dropdown = dropdownRef.current;
      if (isOpen) {
        dropdown.style.display = "block";
        dropdown.style.opacity = "0";
        dropdown.style.transform = "translateY(-10px)";
        dropdown.offsetHeight;
        dropdown.style.transition = "opacity 200ms ease-out, transform 200ms ease-out";
        dropdown.style.opacity = "1";
        dropdown.style.transform = "translateY(0)";
        // Reset focused index when opening
        setFocusedIndex(-1);
      } else {
        dropdown.style.transition = "opacity 150ms ease-in, transform 150ms ease-in";
        dropdown.style.opacity = "0";
        dropdown.style.transform = "translateY(-10px)";
        setTimeout(() => {
          if (!isOpen) {
            dropdown.style.display = "none";
          }
        }, 150);
      }
    }
  }, [isOpen]);

  const handleSelect = (item: FormSelectItem) => {
    setSelected(item);
    setIsOpen(false);
    // This is a user action (manual selection)
    if (onChange) {
      onChange(item, true);
    }
    // Return focus to button after selection
    buttonRef.current?.focus();
  };

  const handleSearchChange = (input?: string) => {
    setSearchValue(input || "");
    // Reset focused index when search changes
    setFocusedIndex(-1);
  };

  // Filter items based on search value
  const { languageMatchedItem, filteredItems } = useMemo(() => {
    const languageMatched = language ? getCountryCodeByLanguage(language) : null;

    if (!searchValue.trim()) {
      // No search - return all items except the language matched one
      const filtered = languageMatched
        ? CountryCodeSelectItems.filter(item => item.value !== languageMatched.value)
        : CountryCodeSelectItems;
      return { languageMatchedItem: languageMatched, filteredItems: filtered };
    }

    const searchLower = searchValue.toLowerCase().trim();
    const filtered = CountryCodeSelectItems.filter(item => {
      const matchesCaption = item.caption.toLowerCase().includes(searchLower);
      const matchesValue = item.value.includes(searchLower);
      const isLanguageMatch = languageMatched && item.value === languageMatched.value;

      // Exclude language matched item from filtered list
      return (matchesCaption || matchesValue) && !isLanguageMatch;
    });

    return { languageMatchedItem: languageMatched, filteredItems: filtered };
  }, [searchValue, language]);

  // Handle keyboard navigation
  const handleListKeyDown = (
    e: React.KeyboardEvent<HTMLLIElement>,
    item: FormSelectItem
  ) => {
    switch (e.key) {
      case "Enter":
      case " ":
        e.preventDefault();
        handleSelect(item);
        break;
      case "Escape":
        e.preventDefault();
        setIsOpen(false);
        buttonRef.current?.focus();
        break;
      case "ArrowDown":
        e.preventDefault();
        moveFocus(1);
        break;
      case "ArrowUp":
        e.preventDefault();
        moveFocus(-1);
        break;
      case "Home":
        e.preventDefault();
        setFocusedIndex(0);
        break;
      case "End":
        e.preventDefault();
        const totalItems = (languageMatchedItem ? 1 : 0) + filteredItems.length;
        setFocusedIndex(totalItems - 1);
        break;
    }
  };

  const moveFocus = (direction: number) => {
    const totalItems = (languageMatchedItem ? 1 : 0) + filteredItems.length;
    setFocusedIndex(prevIndex => {
      let newIndex = prevIndex + direction;
      if (newIndex < 0) {
        newIndex = totalItems - 1;
      }
      if (newIndex >= totalItems) {
        newIndex = 0;
      }
      return newIndex;
    });
  };

  // Focus the correct list item when focusedIndex changes
  useEffect(() => {
    if (isOpen && focusedIndex >= 0 && dropdownRef.current) {
      const listItems = dropdownRef.current.querySelectorAll('li[tabindex="0"]');
      const focusedItem = listItems[focusedIndex] as HTMLElement;
      focusedItem?.focus();
    }
  }, [focusedIndex, isOpen]);

  const getListItemClass = (isLanguageMatch: boolean = false) =>
    classNames(
      "cursor-pointer flex items-center rounded-tl-br-xs p-xs",
      "focus-outline ml-md mr-xs",
      "transition-short-in-out",
      "hover:bg-turquoise-200",
      isLanguageMatch && filteredItems.length > 0 && "mb-sm border-b border-neutral-200"
    );

  return (
    <span className="relative inline-block align-middle">
      {/* Accessible select element */}
      <select
        {...{ inert: "" }} // silly workaround because we're not on react ver 19+ and the fix in eslintrc doesn't work here
        className="sr-only"
        name={name}
        id={id}
        value={selected.value}
        title={title}
        disabled={disabled}
        required={required}
        onChange={e => {
          const selectedItem =
            CountryCodeSelectItems.find(item => item.value === e.target.value) ||
            CountryCodeSelectItems[0];
          handleSelect(selectedItem);
        }}
        onBlur={onBlur}
        tabIndex={-1}
      >
        {CountryCodeSelectItems.map((item, index) => (
          <option key={`options_${index}`} value={item.value}>
            {item.caption}
          </option>
        ))}
      </select>
      <Button
        ref={buttonRef}
        type={ButtonType.Button}
        color={
          disabled
            ? ButtonColor.OutlineNeutral
            : isOpen
              ? ButtonColor.LightBlue
              : ButtonColor.Neutral
        }
        size={ButtonSize.SM}
        variant={ButtonVariant.Normal}
        icon={"/dist/sprite.svg#sys-chevron-down"}
        additionalClass={classNames(
          "[&_svg]:w-lg [&_svg]:h-lg",
          "[&_>_div_>_div]:order-2 [&_>_div_>_span]:order-1",
          "[&_>_div_>_span]:mt-[calc(-1ex+1cap)] [&_>_div_>_span]:text-t-sm",
          "pl-sm pr-xxs min-w-[4.9475rem]"
        )}
        iconRotated={isOpen}
        iconTransition={true}
        disabled={disabled}
        onClick={e => {
          !disabled && setIsOpen(open => !open);
        }}
      >
        {selected ? selected.value : ""}
      </Button>
      <div
        ref={dropdownRef}
        className={classNames(
          "absolute z-component",
          "w-full min-w-[20rem]",
          "pt-md pb-0 mt-sm bg-white",
          "rounded-tl-br-sm shadow-md",
          "overflow-auto @container/countrycodes",
          isOpen ? "block" : "hidden"
        )}
        style={{ display: "none" }}
        role="listbox"
        aria-label={ariaLabel}
      >
        {allowSearch && (
          <div className="mx-md pb-sm border-b border-neutral-200">
            <SearchInput
              name={`${id}_search`}
              placeholder={searchPlaceholder}
              onInput={handleSearchChange}
              additionalClassName="text-sm"
            />
          </div>
        )}
        <ul className="flex flex-col gap-sm pt-md max-h-[30rem] w-[100cqw] overflow-y-auto">
          {languageMatchedItem && (
            <li
              key={"language_match"}
              className={getListItemClass(true)}
              onClick={e => {
                e.stopPropagation();
                e.preventDefault();
                handleSelect(languageMatchedItem);
              }}
              onKeyDown={e => handleListKeyDown(e, languageMatchedItem)}
              tabIndex={0}
              role="option"
              aria-selected={selected.value === languageMatchedItem.value}
            >
              <div className="flex justify-between w-full text-t-neutral-700 text-t-xs">
                <span className="font-t-semi-bold">{languageMatchedItem.caption}</span>
                <span>{languageMatchedItem.value}</span>
              </div>
            </li>
          )}
          {filteredItems.map((item, index) => (
            <li
              key={`results_${index}`}
              className={getListItemClass()}
              onClick={e => {
                e.stopPropagation();
                e.preventDefault();
                handleSelect(item);
              }}
              onKeyDown={e => handleListKeyDown(e, item)}
              tabIndex={0}
              role="option"
              aria-selected={selected.value === item.value}
            >
              <div className="flex justify-between w-full text-t-neutral-700 text-t-xs">
                <span className="font-t-semi-bold">{item.caption}</span>
                <span>{item.value}</span>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </span>
  );
});

export default CountryCodeSelect;
